// Datas em pt-BR no fuso da academia (DESIGN §6: "23 set", "19h12", "há 3 dias"; semana começa na segunda).
// A API manda instantes em UTC ("...Z") e datas do calendário como "AAAA-MM-DD" (SPEC §3).
// O fuso vem de `sessao.academia.fuso`; o padrão é America/Sao_Paulo.

export const FUSO_PADRAO = "America/Sao_Paulo";
const LOCALE = "pt-BR";
const MS_DIA = 86_400_000;

type Instante = string | Date;
const paraDate = (v: Instante) => (typeof v === "string" ? new Date(v) : v);

const cache = new Map<string, Intl.DateTimeFormat>();
function fmt(fuso: string, opcoes: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const chave = fuso + JSON.stringify(opcoes);
  let f = cache.get(chave);
  if (!f) {
    f = new Intl.DateTimeFormat(LOCALE, { timeZone: fuso, ...opcoes });
    cache.set(chave, f);
  }
  return f;
}

function partes(instante: Date, fuso: string) {
  const p: Record<string, string> = {};
  for (const x of fmt(fuso, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instante)) {
    p[x.type] = x.value;
  }
  return {
    ano: p.year!,
    mes: p.month!,
    dia: p.day!,
    hora: p.hour === "24" ? "00" : p.hour!,
    minuto: p.minute!,
  };
}

/** "AAAA-MM-DD" do instante no fuso. */
export function diaLocalDe(instante: Instante, fuso = FUSO_PADRAO): string {
  const p = partes(paraDate(instante), fuso);
  return `${p.ano}-${p.mes}-${p.dia}`;
}

/** Hoje no fuso da academia. */
export function hojeLocal(fuso = FUSO_PADRAO, agora: Date = new Date()): string {
  return diaLocalDe(agora, fuso);
}

/** Data do calendário ("AAAA-MM-DD") como Date ao meio-dia UTC — seguro para formatar sem deslocar o dia. */
function dataCalendario(dia: string): Date {
  return new Date(`${dia}T12:00:00Z`);
}

const MESES_CURTOS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const DIAS_CURTOS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

/** "23 set" a partir de instante (no fuso) ou de "AAAA-MM-DD". */
export function formatarDataCurta(valor: Instante, fuso = FUSO_PADRAO): string {
  const dia = typeof valor === "string" && valor.length === 10 ? valor : diaLocalDe(valor, fuso);
  const [, mes, d] = dia.split("-");
  return `${Number(d)} ${MESES_CURTOS[Number(mes) - 1]}`;
}

/** "23/09/2026" */
export function formatarDataNumerica(valor: Instante, fuso = FUSO_PADRAO): string {
  const dia = typeof valor === "string" && valor.length === 10 ? valor : diaLocalDe(valor, fuso);
  const [ano, mes, d] = dia.split("-");
  return `${d}/${mes}/${ano}`;
}

/** "19h12" */
export function formatarHora(instante: Instante, fuso = FUSO_PADRAO): string {
  const p = partes(paraDate(instante), fuso);
  return `${Number(p.hora)}h${p.minuto}`;
}

/** "23 set, 19h12" */
export function formatarDataHora(instante: Instante, fuso = FUSO_PADRAO): string {
  return `${formatarDataCurta(instante, fuso)}, ${formatarHora(instante, fuso)}`;
}

/** "Ter" */
export function formatarDiaSemanaCurto(dia: string): string {
  return DIAS_CURTOS[dataCalendario(dia).getUTCDay()]!;
}

/** Para aria-label: "terça-feira, 23 de setembro". */
export function formatarDataCompleta(valor: Instante, fuso = FUSO_PADRAO): string {
  const dia = typeof valor === "string" && valor.length === 10 ? valor : diaLocalDe(valor, fuso);
  return fmt("UTC", { weekday: "long", day: "numeric", month: "long" }).format(dataCalendario(dia));
}

/**
 * Cabeçalho do Início: "Quarta, 30 de setembro", "Domingo, 27 de setembro". Sem o "-feira": com ele a data mais
 * longa não cabe ao lado das ações em 360 px (mockup docs/prototipo/inicio-saudacao.html §8).
 */
export function formatarDataCabecalho(valor: Instante, fuso = FUSO_PADRAO): string {
  const t = formatarDataCompleta(valor, fuso).replace("-feira", "");
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** "Setembro" ou "Setembro 2026" a partir de "AAAA-MM". */
export function formatarMes(mes: string, comAno = false): string {
  const nome = fmt("UTC", { month: "long" }).format(dataCalendario(`${mes}-01`));
  const capital = nome.charAt(0).toUpperCase() + nome.slice(1);
  return comAno ? `${capital} ${mes.slice(0, 4)}` : capital;
}

/** Diferença a − b em dias entre datas "AAAA-MM-DD". */
export function diasEntre(a: string, b: string): number {
  return Math.round((dataCalendario(a).getTime() - dataCalendario(b).getTime()) / MS_DIA);
}

/** Soma dias a "AAAA-MM-DD". */
export function somarDias(dia: string, n: number): string {
  return new Date(dataCalendario(dia).getTime() + n * MS_DIA).toISOString().slice(0, 10);
}

/** Segunda-feira da semana ISO do dia. */
export function segundaDaSemana(dia: string): string {
  const dow = dataCalendario(dia).getUTCDay();
  return somarDias(dia, dow === 0 ? -6 : 1 - dow);
}

/**
 * Tempo relativo em caixa de sentença (docs/prototipo/padrao-caixa-texto.md, regras 2 e 3): sozinho começa com
 * maiúscula ("Agora", "Há 5 min", "Há 2 horas", "Ontem", "Há 3 dias", depois "23 set"); `noMeio` para quem
 * encaixa no meio da frase ou depois de "·" ("Ativado há 2 dias", "Manhã · há 20 min").
 */
export function formatarRelativo(
  instante: Instante,
  agora: Date = new Date(),
  fuso = FUSO_PADRAO,
  opcoes: { noMeio?: boolean } = {},
): string {
  const texto = relativoMinusculo(paraDate(instante), agora, fuso);
  return opcoes.noMeio ? texto : texto.charAt(0).toUpperCase() + texto.slice(1);
}

function relativoMinusculo(t: Date, agora: Date, fuso: string): string {
  const seg = Math.round((agora.getTime() - t.getTime()) / 1000);
  if (seg < 60) return "agora";
  const min = Math.floor(seg / 60);
  if (min < 60) return `há ${min} min`;
  const horas = Math.floor(min / 60);
  const dias = diasEntre(diaLocalDe(agora, fuso), diaLocalDe(t, fuso));
  if (dias === 0) return horas === 1 ? "há 1 hora" : `há ${horas} horas`;
  if (dias === 1) return "ontem";
  if (dias < 7) return `há ${dias} dias`;
  return formatarDataCurta(t, fuso);
}

/**
 * Prazo em caixa de sentença (pedido de transferência): "Expira hoje", "Expira amanhã", "Expira em 6 dias"; com
 * `noMeio`, minúsculo ("… · expira em 6 dias"). Conta dias do calendário da academia.
 */
export function formatarExpiracao(
  expiraEm: Instante,
  agora: Date = new Date(),
  fuso = FUSO_PADRAO,
  opcoes: { noMeio?: boolean } = {},
): string {
  const dias = diasEntre(diaLocalDe(expiraEm, fuso), diaLocalDe(agora, fuso));
  const texto = dias <= 0 ? "expira hoje" : dias === 1 ? "expira amanhã" : `expira em ${dias} dias`;
  return opcoes.noMeio ? texto : texto.charAt(0).toUpperCase() + texto.slice(1);
}

/** "Bom dia" (5–11h), "Boa tarde" (12–17h), "Boa noite". */
export function saudacao(agora: Date = new Date(), fuso = FUSO_PADRAO): string {
  const hora = Number(partes(agora, fuso).hora);
  if (hora >= 5 && hora < 12) return "Bom dia";
  if (hora >= 12 && hora < 18) return "Boa tarde";
  return "Boa noite";
}
